/**
 * The rules Sync settles by (issue #25, ADR-0017), as pure functions over small values: what a
 * push result means, whether an incoming Character can be applied here, what a Conflict Copy is
 * called, how Recents merge, and when a tombstone has aged out.
 *
 * Nothing here touches IndexedDB or the network, so every decision the engine makes is testable
 * on its own.
 */
import type { ConflictLoser, PushResult, SyncDocument } from "../../shared/sync-format";
import { TOMBSTONE_LIFETIME_MS } from "../../shared/sync-format";
import type { Character } from "../character/model";
import type { ParkReason, SyncRecord } from "./model";

/**
 * Order two Snapshot release tags ("pf2e-8.5.0" against "pf2e-8.6.0"): -1 when `a` is the older,
 * 1 when it is the newer, 0 when they are the same. Tags from different lines (a different
 * prefix) order by name, so an unknown tag is never mistaken for a newer one of ours.
 */
export function compareSnapshotTags(a: string, b: string): number {
  if (a === b) return 0;
  const prefix = (tag: string): string => {
    const at = tag.search(/\d/);
    return at === -1 ? tag : tag.slice(0, at);
  };
  const pa = prefix(a);
  const pb = prefix(b);
  if (pa !== pb) return pa < pb ? -1 : 1;
  const na = (a.match(/\d+/g) ?? []).map(Number);
  const nb = (b.match(/\d+/g) ?? []).map(Number);
  for (let i = 0; i < Math.max(na.length, nb.length); i++) {
    const x = na[i] ?? -1;
    const y = nb[i] ?? -1;
    if (x !== y) return x < y ? -1 : 1;
  }
  return a < b ? -1 : 1;
}

export type ApplyDecision =
  /** Write it as it stands. */
  | { kind: "apply" }
  /** It comes from an older Snapshot: migrate it on arrival, as an import from a file is migrated (#17). */
  | { kind: "migrate" }
  /** Keep it unapplied, with the reason, until this Device can read it. */
  | { kind: "park"; reason: ParkReason };

/**
 * What to do with a Character another Device sent. A Session open here is never disturbed; a
 * Character from a Snapshot this Device does not have waits, unopened, for the update (ADR-0017).
 */
export function decideCharacterApply(input: { incomingTag: string; deviceTag: string; sessionOpenHere: boolean }): ApplyDecision {
  if (input.sessionOpenHere) return { kind: "park", reason: { kind: "session" } };
  const order = compareSnapshotTags(input.incomingTag, input.deviceTag);
  if (order > 0) return { kind: "park", reason: { kind: "snapshot", tag: input.incomingTag } };
  if (order < 0) return { kind: "migrate" };
  return { kind: "apply" };
}

export type ConflictOutcome =
  /** This Device's push kept the Account; `displaced` is the record it beat, from another Device. */
  | { kind: "won"; winner: SyncDocument; displaced: ConflictLoser }
  /** The Account kept what it had; `mine` is the record this Device just pushed. */
  | { kind: "lost"; winner: SyncDocument; mine: ConflictLoser };

/**
 * Read a conflict the Worker reported. The loser carries no version exactly when it is the push
 * this Device just made (`server/store/documents.ts`), which is what tells the two apart.
 */
export function conflictOutcome(result: Extract<PushResult, { status: "conflict" }>): ConflictOutcome {
  return result.loser.version === null
    ? { kind: "lost", winner: result.winner, mine: result.loser }
    : { kind: "won", winner: result.winner, displaced: result.loser };
}

/** "2026-09-14 14:05" in the Device's own time: short, sortable and free of a locale's surprises. */
export function timeStamp(at: number): string {
  const d = new Date(at);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What a Conflict Copy is called: the Character's name, where the losing version came from, and when. */
export function conflictCopyName(name: string, device: string, at: number): string {
  return `${name} (conflict from ${device}, ${timeStamp(at)})`;
}

/** The losing Character as a Character of its own: a new id, the conflict name, everything else as it was. */
export function conflictCopy(character: Character, id: string, device: string, at: number): Character {
  return { ...character, id, name: conflictCopyName(character.name, device, at) };
}

/** What a record owes the Account. */
export type Pending =
  | "none"
  /** The local record has changed since the Account last saw it. */
  | "save"
  /** The record is gone here and the Account still holds it: push a tombstone. */
  | "delete"
  /** The record is gone here and the Account never held it: forget the bookkeeping. */
  | "untrack";

/**
 * Whether a record owes the Account anything, from its bookkeeping row and the local record's
 * `updatedAt` (null when there is no local record). Conflict Copies owe nothing: they are this
 * Device's own until "Use this copy" makes one the Character again.
 *
 * `inferDeletes` is false for Recents, which fall off the local cap of fifty without being
 * deleted from the Account.
 */
export function pendingFor(row: SyncRecord | undefined, updatedAt: number | null, inferDeletes: boolean): Pending {
  if (row?.conflict) return "none";
  if (row?.deleted && !row.deleted.pushed && updatedAt === null) return "delete";
  if (updatedAt === null) {
    if (row === undefined || row.deleted) return "none";
    if (row.version !== null) return inferDeletes ? "delete" : "untrack";
    // A row holding a record this Device cannot apply yet names nothing gone: it is waiting.
    return row.parked ? "none" : "untrack";
  }
  if (row === undefined || row.syncedAt !== updatedAt) return "save";
  return "none";
}

/** A Recent by the time it was last viewed, for the merge and the cap. */
export interface RecentRow {
  uuid: string;
  viewedAt: number;
}

/**
 * The Recents of two Devices as one list: an Entry seen on both keeps the later time, and the
 * list is cut to `cap`, newest first (ADR-0017). `dropped` falls off this Device only.
 */
export function mergeRecents(local: readonly RecentRow[], incoming: readonly RecentRow[], cap: number): { kept: RecentRow[]; dropped: string[] } {
  const latest = new Map<string, number>();
  for (const row of [...local, ...incoming]) {
    const held = latest.get(row.uuid);
    if (held === undefined || row.viewedAt > held) latest.set(row.uuid, row.viewedAt);
  }
  const all = [...latest].map(([uuid, viewedAt]) => ({ uuid, viewedAt })).sort((x, y) => y.viewedAt - x.viewedAt || (x.uuid < y.uuid ? -1 : 1));
  return { kept: all.slice(0, cap), dropped: all.slice(cap).map((row) => row.uuid) };
}

/** Whether a deletion is past the thirty days Recently Deleted keeps it. */
export function tombstoneExpired(deletedAt: number, now: number): boolean {
  return now - deletedAt >= TOMBSTONE_LIFETIME_MS;
}

/** How a parked record reads on a screen (#26), and in a test's failure message. */
export function describeParkReason(reason: ParkReason): string {
  if (reason.kind === "session") return "a Session is open on this Character here";
  if (reason.kind === "snapshot") return `it needs Snapshot ${reason.tag}`;
  return `it was saved by a newer version of the app (Character schema ${reason.schema})`;
}

/** What makes a parked record apply, for the screen that explains one (#26). */
export function describeParkFix(reason: ParkReason): string {
  if (reason.kind === "session") return "It applies when the Session is closed and this Character is opened again.";
  if (reason.kind === "snapshot") return `It applies once this device has installed Snapshot ${reason.tag}; see About → Updates.`;
  return "It applies once this device has updated to a newer build of the app; see About → Updates.";
}
